import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { buildRoute, ROUTES } from '../../constants/routes';
import { UI, DISPLAY, EASE, h2, sh, pill, tagPackage, tagTest, SECTION_GAP } from '../../styles/tl5';
import { PackagesShimmer } from '../shimmer';

// "Popular tests and packages" — one grid holding both kinds, told apart
// by the tag colour: yellow for a package, mint for a test.
//
// The All / Packages / Tests filter this used to carry is gone; the
// reference offers two destination pills instead and lets the grid show
// the mix. Data is the real catalogue, mapped to the four fields the card
// shows, so ordering follows whatever the API returns.
const metaForTest = (t) => [t.sample, t.fasting ? 'fasting' : 'no fasting', t.tat]
  .filter(Boolean).join(' · ');

const metaForPackage = (p) => [p.paramCount ? `${p.paramCount} tests` : null, p.tat]
  .filter(Boolean).join(' · ');

const PopularPicks = ({ tests = [], packages = [], loading = false }) => {
  const items = [
    ...packages.map((p) => ({
      key: `pk-${p.id}`,
      kind: 'pk',
      tag: 'Package',
      name: p.name,
      meta: metaForPackage(p),
      price: p.price,
      href: buildRoute(ROUTES.PACKAGE_DETAIL, { id: p.id }),
    })),
    ...tests.map((t) => ({
      key: `ts-${t.id}`,
      kind: 'ts',
      tag: 'Test',
      name: t.name,
      meta: metaForTest(t),
      price: t.price,
      href: buildRoute(ROUTES.TEST_DETAIL, { id: t.id }),
    })),
  ];

  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        maxWidth: 1320,
        mx: 'auto',
        pt: SECTION_GAP,
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      <Box sx={{ ...sh, mb: 0, px: 1 }}>
        <Typography component="h2" sx={h2}>Popular tests and packages</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Box component={Link} to={ROUTES.TESTS} sx={pill}>All tests →</Box>
          <Box component={Link} to={ROUTES.PACKAGES} sx={pill}>All packages →</Box>
        </Box>
      </Box>

      {loading ? (
        <PackagesShimmer count={6} min={250} compact />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 250px), 1fr))',
            gap: 1.5,
          }}
        >
          {items.map((it) => (
            <Box
              key={it.key}
              component={Link}
              to={it.href}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                minHeight: 190,
                p: '22px',
                borderRadius: '24px',
                bgcolor: '#FFFFFF',
                color: 'var(--tl-g9)',
                textDecoration: 'none',
                transition: `transform .45s ${EASE}, box-shadow .45s ${EASE}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 30px -16px rgba(11,43,39,0.35)',
                  color: 'var(--tl-g9)',
                },
              }}
            >
              <Box component="span" sx={it.kind === 'pk' ? tagPackage : tagTest}>{it.tag}</Box>

              <Box
                component="span"
                sx={{ flex: 1, fontFamily: DISPLAY, fontSize: 21, fontWeight: 700, lineHeight: 1.15 }}
              >
                {it.name}
              </Box>

              {it.meta && (
                <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                  {it.meta}
                </Box>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                {/* Packages without a price in the catalogue came through
                    as "₹ 0", which reads as free rather than unpriced. */}
                <Box
                  component="span"
                  sx={{
                    fontFamily: DISPLAY,
                    fontSize: Number(it.price) > 0 ? 22 : 15,
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    color: Number(it.price) > 0 ? 'inherit' : 'var(--tl-muted-2)',
                  }}
                >
                  {Number(it.price) > 0 ? `₹ ${Number(it.price).toLocaleString('en-IN')}` : 'Price on request'}
                </Box>
                <Box
                  component="span"
                  sx={{ fontFamily: UI, fontSize: 14, fontWeight: 800, color: 'var(--tl-teal-d)' }}
                >
                  Book →
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default PopularPicks;
